import { type } from 'arktype';

/**
 * Scenario optimization criteria schema
 * Used to configure what the AI should optimize for
 */
export const scenarioCriteriaSchema = type({
  /** Number of scenarios to generate (2-4) */
  scenarioCount: type('2 | 3 | 4').configure({
    description: 'Number of scenario variants to generate. Allowed values: 2, 3, or 4.',
    examples: [2, 3, 4],
  }),
  /** Minimize total labor cost by favoring lower-cost employees */
  minimizeCost: type('boolean').configure({
    description: 'When true, bias the solver toward lower total labor cost by favoring lower-cost employees.',
    examples: [true, false],
  }),
  /** Balance expertise across shifts for optimal skill distribution */
  skillBalance: type('boolean').configure({
    description: 'When true, balance expert and junior staff across shifts for an even skill distribution.',
    examples: [true, false],
  }),
  /** Equal hours distribution across employees */
  fairness: type('boolean').configure({
    description: 'When true, distribute working hours as evenly as possible across eligible employees.',
    examples: [true, false],
  }),
  /** Prioritize 100% shift coverage */
  maxCoverage: type('boolean').configure({
    description: 'When true, prioritize full shift coverage even at the expense of other objectives.',
    examples: [true, false],
  }),
}).configure({
  description: 'Optimization criteria controlling which objectives the AI scenario generator should weight.',
});

export type ScenarioCriteria = typeof scenarioCriteriaSchema.t;

/**
 * AI job data passed to BullMQ
 */
export const aiJobDataSchema = type({
  planId: type('string').configure({
    description: 'Identifier of the plan the scenarios should be generated for.',
    examples: ['plan_01HXYZ123ABC'],
  }),
  userId: type('string').configure({
    description: 'Identifier of the user who submitted the scenario generation job.',
    examples: ['user_01HXYZ456DEF'],
  }),
  criteria: scenarioCriteriaSchema,
  /** Tenant ID for metrics attribution. Populated at job submission from the current
   * request's AsyncLocalStorage tenant context. Optional so existing queued jobs
   * without the field remain valid. */
  'tenantId?': type('string').configure({
    description: 'Tenant identifier used for metrics attribution (nullable for legacy jobs without tenant context).',
    examples: ['tenant_acme_ch'],
  }),
  /** Tenant database URL for worker-side DB context restoration. */
  'databaseUrl?': type('string').configure({
    description: 'Tenant-scoped database URL used by the worker to restore DB context (nullable for legacy jobs).',
    examples: ['postgres://worker@db.internal:5432/tenant_acme'],
  }),
}).configure({
  description: 'BullMQ job payload carrying the data needed to execute an AI scenario generation run.',
});

export type AIJobData = typeof aiJobDataSchema.t;

/**
 * Scenario metrics for evaluation
 */
export const scenarioMetricsSchema = type({
  /** Fairness score (0-100) indicating workload balance */
  fairnessScore: type('number >= 0 & number <= 100').configure({
    description: 'Fairness score between 0 and 100; higher means more even workload distribution.',
    examples: [72, 95],
  }),
  /** Skill match score (0-100) indicating how well skills match requirements */
  skillMatchScore: type('number >= 0 & number <= 100').configure({
    description: 'Skill match score between 0 and 100; higher means better fit between skills and shift requirements.',
    examples: [80, 100],
  }),
  /** Total overtime hours scheduled */
  overtimeHours: type('number >= 0').configure({
    description: 'Total overtime hours scheduled in this scenario (non-negative).',
    examples: [0, 12.5],
  }),
}).configure({
  description: 'Quantitative metrics used to compare and rank generated scenarios.',
});

export type ScenarioMetrics = typeof scenarioMetricsSchema.t;

/**
 * Individual assignment in a scenario
 */
export const assignmentSchema = type({
  /** Shift unit ID being assigned */
  shiftUnitId: type('string').configure({
    description: 'Identifier of the shift unit the employee is being assigned to.',
    examples: ['shift_unit_01HXYZ789'],
  }),
  /** Employee ID being assigned */
  employeeId: type('string').configure({
    description: 'Identifier of the employee being assigned to the shift unit.',
    examples: ['employee_01HXYZABC'],
  }),
  /** AI reasoning for this assignment */
  reason: type('string').configure({
    description: 'Free-form explanation produced by the AI justifying this assignment.',
    examples: ['Lowest-cost qualified employee with matching availability.'],
  }),
}).configure({
  description: 'Single employee-to-shift-unit assignment within a generated scenario.',
});

export type Assignment = typeof assignmentSchema.t;

/**
 * Single scenario result from AI
 */
export const scenarioSchema = type({
  /** Name of the scenario (e.g., "Cost Optimized") */
  name: type('string').configure({
    description: 'Human-readable name of the scenario variant.',
    examples: ['Cost Optimized', 'Fair Distribution'],
  }),
  /** Description of the optimization approach */
  description: type('string').configure({
    description: 'Short narrative describing the optimization approach and trade-offs.',
    examples: ['Minimises overall labor cost by preferring junior staff for low-skill slots.'],
  }),
  /** Total estimated cost in CHF */
  totalCost: type('number >= 0').configure({
    description: 'Total estimated labor cost in CHF for this scenario (non-negative).',
    examples: [12450, 18790.5],
  }),
  /** Coverage percentage (0-100) */
  coveragePercent: type('number >= 0 & number <= 100').configure({
    description: 'Percentage of required shifts that are covered in this scenario (0-100).',
    examples: [95, 100],
  }),
  /** List of shift assignments */
  assignments: assignmentSchema.array().configure({
    description: 'Ordered list of employee-to-shift-unit assignments produced by the scenario.',
    examples: [[]],
  }),
  /** Evaluation metrics */
  metrics: scenarioMetricsSchema,
}).configure({
  description: 'A single scenario variant returned by the AI scenario generator.',
});

export type Scenario = typeof scenarioSchema.t;

/**
 * Complete response from AI provider
 */
export const scenarioResponseSchema = type({
  scenarios: scenarioSchema.array().configure({
    description: 'Array of scenario variants returned by the AI provider, one per requested scenario count.',
    examples: [[]],
  }),
}).configure({
  description: 'Full response payload returned by the AI provider containing all generated scenarios.',
});

export type ScenarioResponse = typeof scenarioResponseSchema.t;

/**
 * AI job result with processing metadata
 */
export const aiJobResultSchema = type({
  scenarios: scenarioSchema.array().configure({
    description: 'Array of generated scenario variants produced during the job run.',
    examples: [[]],
  }),
  processingTimeMs: type('number >= 0').configure({
    description: 'Wall-clock processing time of the job in milliseconds (non-negative).',
    examples: [1250, 5600],
  }),
}).configure({
  description: 'Result payload for a completed AI scenario generation job including runtime metadata.',
});

export type AIJobResult = typeof aiJobResultSchema.t;

/**
 * Generate scenarios request
 */
export const generateScenariosRequestSchema = type({
  planId: type('string').configure({
    description: 'Identifier of the plan to generate scenarios for.',
    examples: ['plan_01HXYZ123ABC'],
  }),
  criteria: scenarioCriteriaSchema,
}).configure({
  description: 'Request body for submitting a scenario generation job.',
});

export type GenerateScenariosRequest = typeof generateScenariosRequestSchema.t;

/**
 * Generate scenarios response
 */
export const generateScenariosResponseSchema = type({
  jobId: type('string').configure({
    description: 'Identifier of the queued BullMQ job; clients poll or subscribe via this id.',
    examples: ['job_01HXYZ999ZZZ'],
  }),
}).configure({
  description: 'Response returned after a scenario generation request is accepted for async processing.',
});

export type GenerateScenariosResponse = typeof generateScenariosResponseSchema.t;

/**
 * Validation helper for scenario criteria
 */
export function validateScenarioCriteria(data: unknown): ScenarioCriteria {
  const result = scenarioCriteriaSchema(data);
  if (result instanceof type.errors) {
    throw new TypeError(`Invalid criteria: ${result.summary}`);
  }
  return result;
}

/**
 * Validation helper for scenario response
 */
export function validateScenarioResponse(data: unknown): ScenarioResponse {
  const result = scenarioResponseSchema(data);
  if (result instanceof type.errors) {
    throw new TypeError(`Invalid AI response: ${result.summary}`);
  }
  return result;
}

/**
 * Validation helper for job data
 */
export function validateAIJobData(data: unknown): AIJobData {
  const result = aiJobDataSchema(data);
  if (result instanceof type.errors) {
    throw new TypeError(`Invalid job data: ${result.summary}`);
  }
  return result;
}
